import React, { memo, useCallback, useState, useRef, useEffect } from "react";
import classnames from "classnames";
import { FormGroup, Intent, InputGroup } from "@blueprintjs/core";
import css from "../form.module.scss";
import { IPostCodeFieldProps } from "../models";
import { FastField, FieldProps, getIn } from "formik";
import { getLocaleSettings } from "../../../../i18n/localeSettings";
import { formatPostalOrZipCode }  from "../../../../utils/dataFormatters";
import { validationMethodFactory, hasRequiredValidation, ValidationType,  findValidation, postCodeMessage } from "../models/validation";
import RequiredIndicator from "../../RequiredIndicator";
import useFormContext from "../FormContext";
import PrintedTextField from "./PrintedTextField";


function isValid(value: string, locale: string) {
  return !postCodeMessage(value, {locale});
}
type  Props<T=any> = FieldProps<T> & { fieldProps: IPostCodeFieldProps };

const BoundPostCodeField = ({ field, form, fieldProps }: Props) => {
  const { locale, isEditing, isPrinting, fieldChangedSignal, fieldPropOverrides } = useFormContext();
  const error = (form.submitCount || getIn(form.touched, field.name)) && getIn(form.errors, field.name);
  const intent = error ? Intent.DANGER : Intent.NONE;
  const isRequired = hasRequiredValidation(fieldProps.validations);
  let [inputValue, setInputValue] = useState(formatPostalOrZipCode(field.value, locale) || '');
  const initialValue = useRef(inputValue);
  const { postCode: settings } = getLocaleSettings(locale);
  const className = classnames(css.formPart);
  const { setFieldValue } = form;
  const disabled = fieldProps.disabled ?? false;

  useEffect(() => {
    if (field.value !== inputValue)
    setInputValue(formatPostalOrZipCode(field.value, locale) || '');
  }, [field.value, inputValue, locale]);

  const onFocus = useCallback((e: React.FocusEvent) => {
    initialValue.current = inputValue;
  }, [inputValue]);

  const onChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value.toLocaleUpperCase(locale);
    setInputValue(value);
    field.onChange(e);
  }, [field, locale]);

  const onBlur = useCallback((e: React.FocusEvent) => {
    field.onBlur(e);

    let normalizedValue = inputValue;
    if (isValid(inputValue, locale)) {
      normalizedValue = formatPostalOrZipCode(inputValue, locale);
      setInputValue(normalizedValue);
      setFieldValue(field.name, normalizedValue);
    }

    normalizedValue !== initialValue.current && fieldChangedSignal.dispatch(field.name, normalizedValue, initialValue.current);
  }, [field, inputValue, locale, setFieldValue, fieldChangedSignal]);

  const control = isPrinting
    ? <PrintedTextField value={inputValue} />
    : <InputGroup
        fill={true}
        intent={intent}
        placeholder={(isEditing && fieldProps.prompt) || undefined}
        disabled={!isEditing || disabled}
        name={field.name}
        value={inputValue}
        maxLength={settings.maxLen}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        {...fieldPropOverrides[field.name]}
      />;

  return (
    <FormGroup
      label={fieldProps.label}
      labelInfo={<RequiredIndicator show={isRequired} isPrinting={isPrinting} />}
      intent={intent}
      helperText={isEditing ? (error || <>&nbsp;</>) : undefined}
      style={fieldProps.style}
      className={className}
    >
      {control}
    </FormGroup>)
}

const PostCodeField = memo((props : IPostCodeFieldProps) => {
  console.assert(props && props.binding);
  const {validations = []} = props;
  if (!findValidation(ValidationType.PostCode, validations)) {
    validations.push({type: ValidationType.PostCode})
  }

  const { locale, isEditing } = useFormContext();
  const validate = isEditing ? validationMethodFactory({ ...props, validations }, locale) : undefined;

  return <FastField name={props.binding} fieldProps={props} component={BoundPostCodeField} validate={validate} />;
});


export default PostCodeField;






